/**
 * HttpOnly cookie auth (fxb_access / fxb_refresh).
 * JWT must NOT live in sessionStorage/localStorage (XSS can read them).
 * TradeSession stores user profile only when authViaCookies is true.
 */
import axios from "axios";
import { getSessionDataOnlyRead } from "utils/common";

export const USE_AUTH_COOKIES =
  String(process.env.REACT_APP_USE_AUTH_COOKIES || "").toLowerCase() === "true";

/** Remember-me: username only (never password). */
export const REMEMBER_LOGIN_KEY = "fxb_remember_login";

const TOKEN_KEYS = [
  "accessToken",
  "AccessToken",
  "refreshToken",
  "RefreshToken",
  "token",
  "Token",
];

function sessionHasUserButNoJwt(s) {
  if (!s) return false;
  const tokens = s.tokens || s.Tokens || {};
  const token =
    tokens.accessToken ||
    tokens.AccessToken ||
    s.accessToken ||
    s.AccessToken ||
    "";
  const hasUser = Boolean(
    s.userId ?? s.UserId ?? s.userName ?? s.UserName ?? s.userCode ?? s.UserCode
  );
  return hasUser && !token;
}

/** Active when build flag is on or TradeSession was saved after cookie login. */
export function isAuthCookieSession(sessionData) {
  if (USE_AUTH_COOKIES) return true;
  const s = sessionData ?? getSessionDataOnlyRead();
  if (Boolean(s?.authViaCookies)) return true;
  return sessionHasUserButNoJwt(s);
}

function readAccessToken(apiData) {
  const tokens = apiData?.tokens || apiData?.Tokens || {};
  return (
    tokens.accessToken ||
    tokens.AccessToken ||
    apiData?.accessToken ||
    apiData?.AccessToken ||
    ""
  );
}

/** Remove JWT fields before writing TradeSession (XSS-safe). */
export function stripJwtFromSessionPayload(data) {
  if (!data || typeof data !== "object") return data;
  const copy = { ...data };

  if (isAuthCookieSession(copy) || USE_AUTH_COOKIES) {
    copy.authViaCookies = true;
    if (copy.tokens || copy.Tokens) {
      const t = { ...(copy.tokens || copy.Tokens) };
      for (const k of TOKEN_KEYS) {
        if (k in t) t[k] = "";
      }
      copy.tokens = t;
      delete copy.Tokens;
    }
    for (const k of TOKEN_KEYS) {
      if (k in copy) copy[k] = "";
    }
  }
  return copy;
}

export function persistTradeSession(data) {
  const safe = stripJwtFromSessionPayload(data);
  sessionStorage.setItem("TradeSession", window.btoa(JSON.stringify(safe)));
  return safe;
}

/** Re-save session without JWT if an old TradeSession still had tokens in storage. */
export function sanitizeStoredTradeSession() {
  const raw = getSessionDataOnlyRead();
  if (!raw) return;
  if (USE_AUTH_COOKIES || raw.authViaCookies || readAccessToken(raw)) {
    persistTradeSession(raw);
  }
}

export function setRememberLoginUsername(username, remember) {
  if (remember && username) {
    sessionStorage.setItem(REMEMBER_LOGIN_KEY, String(username).trim());
  } else {
    sessionStorage.removeItem(REMEMBER_LOGIN_KEY);
  }
  // Legacy insecure cookie — always clear (was username,password readable by JS).
  try {
    document.cookie = "trade=; Path=/; Max-Age=0";
  } catch {
    /* ignore */
  }
}

export function getRememberLoginUsername() {
  try {
    return sessionStorage.getItem(REMEMBER_LOGIN_KEY) || "";
  } catch {
    return "";
  }
}

/** Mark cookie mode on login payload before persistTradeSession. */
export function normalizeLoginPayload(apiData) {
  if (!apiData) return apiData;

  const tokens = apiData.tokens || apiData.Tokens || {};
  if (!apiData.tokens) apiData.tokens = tokens;

  const token = readAccessToken(apiData);
  const hasUserIdentity = Boolean(
    apiData.userId ??
      apiData.UserId ??
      apiData.userName ??
      apiData.UserName ??
      apiData.userCode ??
      apiData.UserCode
  );

  const accessIn = tokens.accessToken ?? tokens.AccessToken ?? apiData.accessToken;
  const refreshIn = tokens.refreshToken ?? tokens.RefreshToken ?? apiData.refreshToken;

  const emptyTokensFromCookieMode =
    !token &&
    hasUserIdentity &&
    (accessIn === undefined || accessIn === null || String(accessIn) === "") &&
    (refreshIn === undefined || refreshIn === null || String(refreshIn) === "");

  if (USE_AUTH_COOKIES || emptyTokensFromCookieMode) {
    apiData.authViaCookies = true;
    if (USE_AUTH_COOKIES) {
      for (const k of TOKEN_KEYS) {
        if (k in apiData) apiData[k] = "";
      }
      if (apiData.tokens) {
        for (const k of TOKEN_KEYS) {
          if (k in apiData.tokens) apiData.tokens[k] = "";
        }
      }
    }
  }

  return apiData;
}

/** @returns {{ ok: true, finalData: object } | { ok: false, message: string }} */
export function validateLoginApiResponse(data) {
  if (data?.status !== 200) {
    return { ok: false, message: data?.message || "Login failed" };
  }

  const finalData = normalizeLoginPayload({ ...(data.data || {}) });
  const token = readAccessToken(finalData);

  if (!token && !isAuthCookieSession(finalData)) {
    return { ok: false, message: "No access token received." };
  }

  return { ok: true, finalData };
}

export function applySessionAuthHeaders(sessionData) {
  if (isAuthCookieSession(sessionData)) {
    delete axios.defaults.headers.common.Authorization;
    return;
  }
  const token = sessionData?.tokens?.accessToken || sessionData?.tokens?.AccessToken;
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function applyAxiosRequestAuth(config) {
  config.headers = config.headers || {};
  config.withCredentials = true;
  if (isAuthCookieSession()) {
    delete config.headers.Authorization;
    delete axios.defaults.headers.common.Authorization;
  }
  return config;
}

let refreshAuthCookiesPromise = null;

function readSessionTokens(sessionData) {
  const s = sessionData || {};
  const t = s.tokens || s.Tokens || {};
  return {
    accessToken:
      t.accessToken ||
      t.AccessToken ||
      s.accessToken ||
      s.AccessToken ||
      "",
    refreshToken:
      t.refreshToken ||
      t.RefreshToken ||
      s.refreshToken ||
      s.RefreshToken ||
      "",
  };
}

function persistRefreshedTokens(tokenData) {
  const current = getSessionDataOnlyRead();
  if (!current || !tokenData || typeof tokenData !== "object") return;

  const cookieMode = isAuthCookieSession(current);

  if (cookieMode) {
    const existingTokens = current.tokens || current.Tokens || {};
    const next = {
      ...current,
      authViaCookies: true,
      tokens: {
        ...existingTokens,
        accessToken: "",
        AccessToken: "",
        refreshToken: "",
        RefreshToken: "",
        expiresOn:
          tokenData.expiresOn ??
          tokenData.ExpiresOn ??
          existingTokens.expiresOn ??
          existingTokens.ExpiresOn,
        issuedOn:
          tokenData.issuedOn ??
          tokenData.IssuedOn ??
          existingTokens.issuedOn ??
          existingTokens.IssuedOn,
        expiredOn:
          tokenData.expiredOn ??
          tokenData.ExpiredOn ??
          existingTokens.expiredOn ??
          existingTokens.ExpiredOn,
      },
    };
    delete next.Tokens;
    persistTradeSession(next);
    applySessionAuthHeaders(next);
    return;
  }

  const existingTokens = current.tokens || current.Tokens || {};
  const mergedTokens = {
    ...existingTokens,
    accessToken:
      tokenData.accessToken ??
      tokenData.AccessToken ??
      existingTokens.accessToken ??
      existingTokens.AccessToken ??
      "",
    refreshToken:
      tokenData.refreshToken ??
      tokenData.RefreshToken ??
      existingTokens.refreshToken ??
      existingTokens.RefreshToken ??
      "",
    expiresOn:
      tokenData.expiresOn ??
      tokenData.ExpiresOn ??
      existingTokens.expiresOn ??
      existingTokens.ExpiresOn,
    issuedOn:
      tokenData.issuedOn ??
      tokenData.IssuedOn ??
      existingTokens.issuedOn ??
      existingTokens.IssuedOn,
    expiredOn:
      tokenData.expiredOn ??
      tokenData.ExpiredOn ??
      existingTokens.expiredOn ??
      existingTokens.ExpiredOn,
  };

  const next = {
    ...current,
    tokens: mergedTokens,
  };
  delete next.Tokens;
  persistTradeSession(next);
  applySessionAuthHeaders(next);
}

/**
 * Standard flow: no background refresh while app is idle/minimized.
 * New access JWT is minted only when an API call gets 401 and this runs (see setupAxiosInterceptors).
 */
export function refreshAuthCookiesOnce() {
  if (refreshAuthCookiesPromise) return refreshAuthCookiesPromise;

  const raw = getSessionDataOnlyRead();
  const cookieMode = isAuthCookieSession(raw);
  const { accessToken, refreshToken } = readSessionTokens(raw);

  // Cookie mode can refresh without payload; JWT mode needs both tokens in body.
  if (!cookieMode && (!accessToken || !refreshToken)) {
    return Promise.resolve(false);
  }

  const payload = cookieMode
    ? {}
    : {
        accessToken,
        refreshToken,
      };

  const doRefresh = (retryStaleAccess) =>
    axios
      .post("user/refresh-token", payload, { withCredentials: true })
      .then((res) => {
        if (res?.data?.status === 200) {
          persistRefreshedTokens(res?.data?.data || {});
          return true;
        }
        const msg = String(res?.data?.message || "");
        if (
          !retryStaleAccess &&
          msg.toLowerCase().includes("token doesn't match the saved token")
        ) {
          return doRefresh(true);
        }
        return false;
      })
      .catch((err) => {
        const msg = String(err?.response?.data?.message || "");
        if (
          !retryStaleAccess &&
          msg.toLowerCase().includes("token doesn't match the saved token")
        ) {
          return doRefresh(true);
        }
        return false;
      });

  refreshAuthCookiesPromise = doRefresh(false).finally(() => {
    refreshAuthCookiesPromise = null;
  });

  return refreshAuthCookiesPromise;
}
